// src/components/inbox/DataTab.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "../ui";
import {
  calendarDate,
  formatMoney,
  instantDate,
  shortName,
  signedAmount,
} from "./inboxFormat";

function Total({ label, value }) {
  return (
    <div data-slot="inbox-total" className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-2">
      <span className="tw-text-xs tw-text-text-muted">{label}</span>
      <span className="tw-text-sm tw-font-bold tw-text-text">{formatMoney(value)}</span>
    </div>
  );
}

// Two sources, never blended: the figures the client typed at onboarding,
// and recent activity from the bank.
export default function DataTab({ clientId, data, client }) {
  const own = data.ownFigures;
  const activity = data.recentActivity || [];
  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h3 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">{`Data · ${shortName(client)}`}</h3>
          <p className="tw-m-0 tw-text-xs tw-text-text-muted">Two sources, kept apart.</p>
        </div>
        {data.canOpenDashboard && (
          <Button
            as={Link}
            variant="text"
            size="sm"
            to={`/dashboard/${clientId}`}
          >
            Open dashboard
          </Button>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface-sunken tw-p-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Client's own figures</span>
          {own?.editedAt && (
            <span className="tw-m-0 tw-text-xs tw-text-text-muted">{`edited ${instantDate(own.editedAt)}`}</span>
          )}
        </div>
        {own ? (
          <>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2">
              <Total label="Investments" value={own.totals.investments} />
              <Total label="Debts" value={own.totals.debts} />
              <Total label="Investments minus debts" value={own.totals.net} />
            </div>
            <ul className="tw-m-0 tw-list-none tw-p-0">
              {own.investments.map((r, i) => (
                <li key={`investment-${i}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                  <span>{r.name}</span>
                  <span>{formatMoney(r.value)}</span>
                </li>
              ))}
              {own.debts.map((r, i) => (
                <li key={`debt-${i}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                  <span>{r.name}</span>
                  <span className="tw-text-warning">{formatMoney(-r.value)}</span>
                </li>
              ))}
            </ul>
            <p className="tw-m-0 tw-text-xs tw-text-text-muted">
              Typed by the client at onboarding and whenever they edit it. Not
              from Plaid.
            </p>
          </>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">
            Onboarding not finished; no figures yet.
          </p>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface-sunken tw-p-3">
        <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Recent activity</div>
        {activity.length > 0 ? (
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {activity.map((t, i) => (
              <li key={`activity-${i}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                <span>
                  {t.name}
                  {t.pending && (
                    <Badge className="tw-ml-2">pending</Badge>
                  )}
                </span>
                <span className="tw-m-0 tw-text-xs tw-text-text-muted">{calendarDate(t.date)}</span>
                <span>{signedAmount(t)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">No transactions yet.</p>
        )}
      </div>

      <p className="tw-m-0 tw-text-xs tw-text-text-muted">
        Not held by the app: interest rates, goal targets and progress,
        emergency cushion. Silence here is not zero.
      </p>
    </div>
  );
}
